'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { LockIcon, CheckIcon } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm, Controller } from 'react-hook-form';
import type { LocaleCode, SimpleSectionContent } from '@/types/sections';
import { pickLocalizedList, pickLocalizedString } from '@/lib/locale';
import { db } from '@/lib/firebase.client';
import { doc, getDoc } from 'firebase/firestore';
import { CONFIDENTIAL_CODE_STORAGE_KEY } from '@/lib/constants';

// Evervault card visualization
import { EvervaultCard } from './ui/evervault-card';

export function JoinSection({
  content,
  locale,
}: {
  content: SimpleSectionContent;
  locale: LocaleCode;
}) {
  const [open, setOpen] = useState(false);
  const [hasValidatedCode, setHasValidatedCode] = useState(false);
  const introParagraphs = pickLocalizedList(content.introParagraphs, locale);
  const ctaLabel = pickLocalizedString(content.ctaLabel, locale, 'Join The Club →');
  const title = pickLocalizedString(content.title, locale, 'Rejoindre Uncommon 12');
  const subtitle = pickLocalizedString(content.subtitle, locale);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const stored = window.localStorage.getItem(CONFIDENTIAL_CODE_STORAGE_KEY);
    if (stored === 'true') {
      setHasValidatedCode(true);
    }
  }, []);

  const tabsCount = hasValidatedCode ? 1 : 2;
  const showInterviewTab = !hasValidatedCode;
  const tabsListClass =
    tabsCount === 1 ? 'w-full grid grid-cols-1 gap-2 h-15' : 'w-full grid grid-cols-2 gap-2 h-15';

  return (
    <section className=' px-4 md:px-8 lg:px-16 xl:px-30 w-full'>
      {/* <div className='mx-auto max-w-2xl text-center mb-8 w-fit'>
        <div className='flex justify-center mb-4'>
          <div className='w-fit rounded-lg border border-[#FCD2C3] p-2 bg-white/80 dark:bg-zinc-900/80'>
            <LockIcon className='h-5 w-5 text-[#FCD2C3]' />
          </div>
        </div>
        <h2 className='text-4xl font-bold text-black dark:text-white mb-4'>
          Rejoindre Uncommon 12
        </h2>
        <p className='text-xl text-neutral-600 dark:text-neutral-400 max-w-3xl mx-auto'>
          Uncommon 12 n’est pas un club fermé — c’est un cercle confidentiel, basé sur les affinités
          choisies.
        </p>
      </div> */}
      <div className='max-w-2xl mx-auto'>
        <div className='border border-black/[0.2] dark:border-white/[0.2] flex flex-col items-start mx-auto p-4 relative'>
          <EvervaultCard src='/logo.svg' />
          <div className='flex flex-col gap-5'>
            <div>
              {subtitle && (
                <p className='dark:text-white text-black mt-4 text-lg'>{subtitle}</p>
              )}
              {introParagraphs.map((text, idx) => (
                <p key={idx} className='dark:text-white text-black mt-4 text-lg'>
                  {text}
                </p>
              ))}
            </div>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button
                  size='lg'
                  className='mt-2 text-lg font-medium w-full'
                  style={{ cursor: 'pointer' }}
                >
                  {ctaLabel}
                </Button>
              </DialogTrigger>
              <DialogContent className='w-full max-w-6xl sm:max-w-xl rounded-2xl shadow-2xl px-0 overflow-hidden'>
                <DialogHeader className='px-8 pt-8 pb-2'>
                  <DialogTitle className='flex items-center gap-2 text-2xl font-bold'>
                    <LockIcon className='h-5 w-5' />
                    {title}
                  </DialogTitle>
                </DialogHeader>
                <div className='px-2 md:px-8 pb-8'>
                  <Tabs defaultValue='code' className=''>
                    <TabsList className={tabsListClass}>
                      <TabsTrigger
                        value='code'
                        className='flex items-center gap-2 justify-center text-lg'
                      >
                        J’ai un code
                      </TabsTrigger>
                      {showInterviewTab && (
                        <TabsTrigger
                          value='interview'
                          className='flex items-center gap-2 justify-center text-lg'
                        >
                          Demander un entretien
                        </TabsTrigger>
                      )}
                    </TabsList>

                    <TabsContent value='code'>
                      <JoinWithCode
                        locale={locale}
                        onSuccess={() => setOpen(false)}
                        onValidated={() => setHasValidatedCode(true)}
                      />
                    </TabsContent>
                    {showInterviewTab && (
                      <TabsContent value='interview'>
                        <InterviewStepper onSuccess={() => setOpen(false)} />
                      </TabsContent>
                    )}
                  </Tabs>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>
    </section>
  );
}

// --- CODE TAB ---
function JoinWithCode({
  locale,
  onSuccess,
  onValidated,
}: {
  locale: LocaleCode;
  onSuccess: () => void;
  onValidated: () => void;
}) {
  const [submitted, setSubmitted] = useState(false);
  const [autoValidated, setAutoValidated] = useState(false);
  const [loadingCode, setLoadingCode] = useState(true);
  const [remoteCode, setRemoteCode] = useState('');
  const [remoteCodeError, setRemoteCodeError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    clearErrors,
  } = useForm<{ code: string }>({ mode: 'onSubmit' });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const stored = window.localStorage.getItem(CONFIDENTIAL_CODE_STORAGE_KEY);
    if (stored === 'true') {
      setSubmitted(true);
      setAutoValidated(true);
      onValidated();
    }
  }, [onValidated]);

  useEffect(() => {
    let active = true;
    async function fetchCode() {
      try {
        setRemoteCodeError(null);
        const docId = process.env.NEXT_PUBLIC_WEBSITE_SETTINGS_DOC_ID || 'default';
        const ref = doc(db, 'website-settings', docId);
        const snap = await getDoc(ref);
        if (!active) return;
        const rawCode = snap.data()?.confidentialCode;
        const normalized = typeof rawCode === 'string' ? rawCode.trim() : '';
        setRemoteCode(normalized);
        if (!normalized) {
          setRemoteCodeError("Aucun code confidentiel n'est configuré pour le moment.");
        }
      } catch (error) {
        console.error('Failed to load confidential code', error);
        if (active) {
          setRemoteCodeError(
            'Impossible de vérifier le code pour le moment. Merci de réessayer.'
          );
        }
      } finally {
        if (active) {
          setLoadingCode(false);
        }
      }
    }
    fetchCode();
    return () => {
      active = false;
    };
  }, []);

  const onSubmit = ({ code }: { code: string }) => {
    clearErrors('code');

    if (loadingCode) {
      setError('code', { type: 'manual', message: 'Chargement du code...' });
      return;
    }

    if (remoteCodeError || !remoteCode) {
      setError('code', {
        type: 'manual',
        message:
          remoteCodeError ?? "Aucun code confidentiel n'est disponible pour le moment.",
      });
      return;
    }

    const normalizedInput = (code || '').trim().toLowerCase();
    const normalizedRemote = remoteCode.trim().toLowerCase();

    if (!normalizedInput) {
      setError('code', { type: 'required', message: 'Ce champ est requis' });
      return;
    }

    if (normalizedInput !== normalizedRemote) {
      setError('code', { type: 'validate', message: 'Code confidentiel invalide' });
      return;
    }

    setSubmitted(true);
    try {
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(CONFIDENTIAL_CODE_STORAGE_KEY, 'true');
      }
    } catch (error) {
      console.warn('Unable to persist confidential code status', error);
    }
    onValidated();
    setTimeout(onSuccess, 1500);
  };

  if (submitted) {
    return (
      <motion.div
        key='success'
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0 }}
        className='text-center flex flex-col gap-5 items-center py-10'
      >
        <CheckIcon className='w-12 h-12 text-green-600 mb-2' />
        <p className='text-xl font-bold text-black dark:text-white'>
          Bienvenue dans l’univers Uncommon 12 !
        </p>
        <p className='text-neutral-600 dark:text-neutral-300 max-w-md'>
          {autoValidated
            ? 'Votre code confidentiel a déjà été confirmé sur cet appareil.'
            : 'Votre code est validé. Choisissez votre prochaine étape pour continuer.'}
        </p>
        <div className='flex flex-col gap-3 w-full max-w-xs'>
          <Button asChild className='w-full' onClick={onSuccess}>
            <Link href={`/${locale}/account/login`}>Accéder à l’espace membre</Link>
          </Button>
          <Button asChild variant='outline' className='w-full' onClick={onSuccess}>
            <Link href={`/${locale}/account/register`}>Créer un compte</Link>
          </Button>
        </div>
      </motion.div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className='flex flex-col gap-6'>
      <p className='text-lg font-medium mb-2 text-black dark:text-white'>
        Vous avez reçu un code d’invitation ?
      </p>
      <p className='text-neutral-600 dark:text-neutral-400'>
        Entrez-le ci-dessous pour accéder à la partie Admission, en quelques clics.
      </p>
      {remoteCodeError && (
        <div className='rounded-xl bg-red-500/10 text-red-600 dark:text-red-400 text-sm p-3'>
          {remoteCodeError}
        </div>
      )}
      <Input
        placeholder='Saisissez votre code confidentiel'
        {...register('code', { required: 'Ce champ est requis' })}
        className='mt-3'
        disabled={loadingCode || Boolean(remoteCodeError)}
      />
      {errors.code && <span className='text-red-500 text-sm'>{errors.code.message}</span>}
      <Button
        type='submit'
        className='w-full mt-2'
        disabled={loadingCode || Boolean(remoteCodeError)}
      >
        {loadingCode ? 'Chargement...' : 'Continuer'}
      </Button>
    </form>
  );
}

// --- INTERVIEW TAB with STEPPER ---
const STEPS = [
  'Vos informations',
  'Vos motivations',
  'Traits principaux',
  'Centres d’intérêt',
  'Disponibilités',
];

type InterviewFormValues = {
  nom: string;
  email: string;
  telephone: string;
  mode_contact: string;
  telephone_contact: string;
  email_visio: string;
  motivations: string[];
  pro_details: string[];
  traits: string[];
  interets: string[];
  disponibilites: string[];
  message: string;
};

function InterviewStepper({ onSuccess }: { onSuccess: () => void }) {
  const [step, setStep] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);

  const {
    control,
    handleSubmit,
    watch,
    register,
    getValues,
    formState: { errors },
  } = useForm<InterviewFormValues>({
    defaultValues: {
      nom: '',
      email: '',
      telephone: '',
      mode_contact: '',
      telephone_contact: '',
      email_visio: '',
      motivations: [] as string[],
      pro_details: [] as string[],
      traits: [] as string[],
      interets: [] as string[],
      disponibilites: [] as string[],
      message: '',
    },
    mode: 'onTouched',
  });

  const mode_contact = watch('mode_contact');

  const onSubmit = async (values: InterviewFormValues) => {
    // If not on last step, move forward instead of submitting
    if (step < STEPS.length - 1) {
      setStep((s) => Math.min(STEPS.length - 1, s + 1));
      return;
    }

    setSendError(null);
    setIsSending(true);
    try {
      const payload = {
        to: 'contact@uncommon12.ch',
        subject: `Demande d'entretien - ${values.nom || values.email || 'Candidat'}`,
        bodyTemplate: 'interview-request',
        layoutTemplate: 'contact',
        templateData: {
          name: values.nom,
          email: values.email,
          phone: values.telephone,
          contact_mode: values.mode_contact || 'Non précisé',
          contact_detail:
            values.mode_contact === 'Téléphone'
              ? values.telephone_contact || values.telephone
              : values.mode_contact === 'Visio'
                ? values.email_visio || values.email
                : values.telephone,
          motivations: (values.motivations ?? []).join(', ') || '—',
          pro_details: (values.pro_details ?? []).join(', ') || '—',
          traits: (values.traits ?? []).join(', ') || '—',
          interets: (values.interets ?? []).join(', ') || '—',
          disponibilites: (values.disponibilites ?? []).join(', ') || '—',
          message: values.message || '—',
        },
      };

      const res = await fetch('/api/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        const rawError = data?.error;
        const msg =
          typeof rawError === 'string'
            ? rawError
            : rawError?.message || rawError?.response || 'Envoi impossible';
        throw new Error(msg);
      }

      setSubmitted(true);
      setTimeout(onSuccess, 2500);
    } catch (error) {
      console.error('Interview request send error', error);
      setSendError(error instanceof Error ? error.message : 'Envoi impossible');
    } finally {
      setIsSending(false);
    }
  };

  if (submitted) {
    return (
      <motion.div
        key='success'
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0 }}
        className='text-center flex flex-col gap-4 items-center py-10'
      >
        <CheckIcon className='w-12 h-12 text-green-600 mb-2' />
        <p className='text-xl font-bold text-black dark:text-white'>
          Votre demande d’adhésion nous est bien parvenue.
        </p>
        <p className='text-neutral-600 dark:text-neutral-300'>
          Nous reviendrons vers vous dans un délai de 2 à 4 jours ouvrés afin de convenir ensemble
          d’un premier échange.
        </p>
        <p className='text-neutral-600 dark:text-neutral-300 mt-2'>
          Au plaisir de vous rencontrer,
        </p>
        <span className='font-semibold text-neutral-800 dark:text-neutral-200'>
          Meet, Share and Live, differently.
          <br />
          Uncommon 12 – Geneva
        </span>
      </motion.div>
    );
  }

  return (
    <form className='flex flex-col gap-6' onSubmit={handleSubmit(onSubmit)} autoComplete='off'>
      {/* Stepper */}
      <Stepper activeStep={step} steps={STEPS} />
      <div className='py-2'>
        <AnimatePresence mode='wait'>
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 32 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -32 }}
            transition={{ duration: 0.3 }}
            className='flex flex-col gap-6'
          >
            {step === 0 && (
              <>
                <p className='font-medium text-lg text-black dark:text-white'>
                  1. Vos informations
                </p>
                <div className='flex flex-col gap-3'>
                  <Input
                    placeholder='Prénom & Nom'
                    {...register('nom', { required: 'Champ requis' })}
                  />
                  {errors.nom && <span className='text-red-500 text-sm'>{errors.nom.message}</span>}
                  <Input
                    placeholder='Adresse email'
                    type='email'
                    {...register('email', { required: 'Champ requis' })}
                  />
                  {errors.email && (
                    <span className='text-red-500 text-sm'>{errors.email.message}</span>
                  )}
                  <Input
                    placeholder='Numéro de téléphone'
                    type='tel'
                    {...register('telephone', { required: 'Champ requis' })}
                  />
                  {errors.telephone && (
                    <span className='text-red-500 text-sm'>{errors.telephone.message}</span>
                  )}

                  <label className='text-sm font-semibold mt-2 mb-1'>
                    Quel mode de contact préférez-vous ?
                  </label>
                  <Controller
                    name='mode_contact'
                    control={control}
                    rules={{ required: 'Sélectionnez un mode' }}
                    render={({ field }) => (
                      <RadioGroup
                        value={field.value}
                        onValueChange={field.onChange}
                        className='flex gap-3 flex-wrap'
                      >
                        <label
                          className='flex items-center gap-2 cursor-pointer'
                          htmlFor='presentiel'
                        >
                          <RadioGroupItem
                            value='Présentiel'
                            id='presentiel'
                            className='border border-gray-400'
                          />
                          Présentiel
                        </label>
                        <label className='flex items-center gap-2 cursor-pointer' htmlFor='tel'>
                          <RadioGroupItem
                            value='Téléphone'
                            id='tel'
                            className='border border-gray-400'
                          />
                          Téléphone
                        </label>
                        <label className='flex items-center gap-2 cursor-pointer' htmlFor='visio'>
                          <RadioGroupItem
                            value='Visio'
                            id='visio'
                            className='border border-gray-400'
                          />
                          Visio
                        </label>
                      </RadioGroup>
                    )}
                  />
                  {errors.mode_contact && (
                    <span className='text-red-500 text-sm'>
                      {String(errors.mode_contact.message)}
                    </span>
                  )}

                  {/* Conditional fields */}
                  {mode_contact === 'Présentiel' && (
                    <div className='bg-neutral-100 dark:bg-neutral-800 p-3 rounded-xl mt-2 text-sm'>
                      📍 Les échanges se déroulent au <b>Base Geneva Hôtel</b>
                      <br />
                      Rue du Jura 13, 1201 Genève
                    </div>
                  )}
                  {mode_contact === 'Téléphone' && (
                    <Input
                      className='mt-2'
                      placeholder='Merci d’indiquer votre numéro préféré pour l’appel'
                      {...register('telephone_contact', { required: 'Champ requis pour appel' })}
                    />
                  )}
                  {mode_contact === 'Visio' && (
                    <Input
                      className='mt-2'
                      placeholder='Merci d’indiquer l’adresse email pour recevoir le lien'
                      {...register('email_visio', { required: 'Champ requis pour visio' })}
                    />
                  )}
                  <Input
                    className='mt-2'
                    placeholder='Message facultatif...'
                    {...register('message')}
                  />
                </div>
              </>
            )}
            {step === 1 && (
              <>
                <p className='font-medium text-lg text-black dark:text-white'>
                  2. Vos motivations principales (plusieurs réponses possibles)
                </p>
                <div className='flex flex-col gap-2'>
                  <Controller
                    name='motivations'
                    control={control}
                    render={({ field }) => (
                      <>
                        <CheckboxGroup
                          options={[
                            {
                              value: 'Passer du bon temps',
                              label:
                                'Passer du bon temps avec des personnes qui partagent les mêmes affinités',
                            },
                            {
                              value: 'Élargir cercle',
                              label:
                                'Élargir mon cercle d’amis et m’intégrer davantage dans la vie genevoise',
                            },
                            {
                              value: 'Échanger pro',
                              label: 'Échanger sur des opportunités professionnelles',
                            },
                          ]}
                          value={field.value ?? []}
                          onChange={field.onChange}
                        />
                        {/* Show pro details if selected */}
                        {(field.value ?? []).includes('Échanger pro') && (
                          <div className='pl-2 border-l mt-2 space-y-2'>
                            <span className='font-semibold text-sm'>Précisez :</span>
                            <Controller
                              name='pro_details'
                              control={control}
                              render={({ field: proField }) => (
                                <CheckboxGroup
                                  options={[
                                    {
                                      value: 'Identifier clients',
                                      label: 'Identifier des clients',
                                    },
                                    {
                                      value: 'Identifier partenaires',
                                      label: 'Identifier des partenaires ou investisseurs',
                                    },
                                    {
                                      value: 'Collaborations',
                                      label: 'Collaborations ou co-branding',
                                    },
                                    {
                                      value: 'Nouveau poste',
                                      label: 'Trouver un nouveau challenge ou poste',
                                    },
                                    {
                                      value: 'Temps pour moi',
                                      label:
                                        'Trouver enfin du temps pour moi / pour voir mes amis, collègues, proches',
                                    },
                                    { value: 'Amoureuse', label: 'Faire une rencontre amoureuse' },
                                  ]}
                                  value={proField.value ?? []}
                                  onChange={proField.onChange}
                                />
                              )}
                            />
                          </div>
                        )}
                      </>
                    )}
                  />
                  {errors.motivations && (
                    <span className='text-red-500 text-sm'>
                      {String(errors.motivations.message)}
                    </span>
                  )}
                </div>
              </>
            )}
            {step === 2 && (
              <>
                <p className='font-medium text-lg text-black dark:text-white'>
                  3. Trois traits qui vous définissent le mieux (choisissez 3)
                </p>
                <div className='grid grid-cols-2 gap-2'>
                  <Controller
                    name='traits'
                    control={control}
                    rules={{
                      validate: (traits) =>
                        traits?.length === 3 ? true : 'Choisissez exactement 3 traits',
                    }}
                    render={({ field }) => (
                      <CheckboxGroup
                        options={[
                          { value: 'Authentique', label: 'Authentique' },
                          { value: 'Sociable', label: 'Sociable' },
                          { value: 'Dynamique', label: 'Dynamique' },
                          { value: 'Leader', label: 'Leader naturel / animateur de groupe' },
                          { value: 'Participant', label: 'Bon participant / suiveur enthousiaste' },
                          { value: 'Humour', label: 'A de l’humour' },
                          { value: 'Célébration', label: 'Aime la célébration' },
                          { value: 'Sportif', label: 'Sportif' },
                          { value: 'Curieux', label: 'Curieux' },
                          { value: 'Surpris', label: 'Aime être surpris' },
                          { value: 'Apprendre', label: 'Aime apprendre' },
                        ]}
                        value={field.value ?? []}
                        onChange={field.onChange}
                        max={3}
                      />
                    )}
                  />
                  {errors.traits && (
                    <span className='text-red-500 text-sm'>{String(errors.traits.message)}</span>
                  )}
                </div>
              </>
            )}
            {step === 3 && (
              <>
                <p className='font-medium text-lg text-black dark:text-white'>
                  4. Vos centres d’intérêt majeurs (plusieurs choix possibles)
                </p>
                <div className='grid grid-cols-2 gap-2'>
                  <Controller
                    name='interets'
                    control={control}
                    render={({ field }) => (
                      <CheckboxGroup
                        options={[
                          { value: 'Sport', label: 'Sport' },
                          { value: 'Œnologie', label: 'Œnologie' },
                          { value: 'Jeux', label: 'Jeux' },
                          { value: 'Bien-être', label: 'Bien-être / relaxation' },
                          { value: 'Musique', label: 'Musique' },
                          { value: 'Échanges humains', label: 'Les échanges humains simplement' },
                          { value: 'Bons repas', label: 'Les bons repas' },
                          { value: 'Mode', label: 'Mode' },
                          {
                            value: 'Culture',
                            label: 'Culture (théâtre, cinéma, lecture, expositions)',
                          },
                        ]}
                        value={field.value ?? []}
                        onChange={field.onChange}
                      />
                    )}
                  />
                </div>
              </>
            )}
            {step === 4 && (
              <>
                <p className='font-medium text-lg text-black dark:text-white'>
                  5. Vous êtes le plus souvent disponible...
                </p>
                <div className='flex flex-col gap-2'>
                  <Controller
                    name='disponibilites'
                    control={control}
                    render={({ field }) => (
                      <CheckboxGroup
                        options={[
                          { value: 'Matin', label: 'Le matin / petit-déjeuner' },
                          { value: 'Lunchs', label: 'Les lunchs' },
                          { value: 'Apéritifs', label: 'Les apéritifs' },
                          { value: 'Dîners', label: 'Les dîners' },
                          { value: 'Semaine', label: 'En semaine' },
                          { value: 'Week-end', label: 'Le week-end' },
                        ]}
                        value={field.value ?? []}
                        onChange={field.onChange}
                      />
                    )}
                  />
                </div>
              </>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
      {sendError && (
        <div className='rounded-xl bg-red-500/10 text-red-600 dark:text-red-400 text-sm p-3'>
          {sendError}
        </div>
      )}
      {/* Stepper Controls */}
      <DialogFooter className='gap-2 pt-2 flex-row-reverse'>
        {step > 0 && (
          <Button
            type='button'
            variant='secondary'
            onClick={() => setStep((s) => s - 1)}
            disabled={isSending}
          >
            ← Précédent
          </Button>
        )}
        {step < STEPS.length - 1 ? (
          <Button
            type='submit'
            className='ml-2'
            disabled={
              isSending ||
              (step === 0 &&
                (!getValues('nom') ||
                  !getValues('email') ||
                  !getValues('telephone') ||
                  !getValues('mode_contact') ||
                  (getValues('mode_contact') === 'Téléphone' && !getValues('telephone_contact')) ||
                  (getValues('mode_contact') === 'Visio' && !getValues('email_visio'))))
            }
          >
            Suivant →
          </Button>
        ) : (
          <Button type='submit' className='ml-2' disabled={isSending}>
            {isSending ? 'Envoi...' : 'Soumettre la demande'}
          </Button>
        )}
      </DialogFooter>
    </form>
  );
}

// --- SMALL COMPONENTS ---
function Stepper({ activeStep, steps }: { activeStep: number; steps: string[] }) {
  return (
    <div className='flex items-center gap-2 justify-center mt-5'>
      {steps.map((label, i) => (
        <button
          key={label}
          className={cn(
            'w-8 h-8 flex items-center justify-center rounded-full border-2 font-bold text-base transition',
            activeStep === i
              ? 'border-white-500 bg-white-100 dark:bg-white-900 text-white-800'
              : 'border-neutral-300 bg-white dark:bg-zinc-900 text-neutral-400 hover:border-white-400'
          )}
          type='button'
          aria-label={label}
          // onClick={() => setStep(i)}
        >
          {i + 1}
        </button>
      ))}
    </div>
  );
}

function CheckboxGroup({
  options,
  value,
  onChange,
  max,
}: {
  options: { value: string; label: string }[];
  value: string[];
  onChange: (v: string[]) => void;
  max?: number;
}) {
  return (
    <div className='flex flex-col gap-1'>
      {options.map((opt) => (
        <label key={opt.value} className='flex items-center gap-2 cursor-pointer'>
          <Checkbox
            checked={value?.includes(opt.value)}
            onCheckedChange={(checked) => {
              if (checked) {
                if (!value.includes(opt.value) && (!max || value.length < max)) {
                  onChange([...value, opt.value]);
                }
              } else {
                onChange(value.filter((v) => v !== opt.value));
              }
            }}
            disabled={(max && !value.includes(opt.value) && value.length >= max) === true}
          />
          {opt.label}
        </label>
      ))}
      {max && <span className='text-xs text-neutral-400'>(Max {max} choix)</span>}
    </div>
  );
}
